import React, { createContext, useContext, useState, ReactNode } from 'react'

export type HomeType = 'single-family' | 'condo-other' | null
export type Challenge = 
  | 'high-energy-bills'
  | 'unexpected-repairs'
  | 'drafty-windows'
  | 'plumbing-leaks'
  | 'maintenance-upkeep'
  | 'trusted-contractors'

export type PackageType = 'refresh' | 'deluxe' | null

interface FunnelState {
  homeType: HomeType
  challenges: Challenge[]
  selectedPackage: PackageType
  email: string
  fullName: string
  phone: string
  address: string
  preferredServiceWindow: string
}

interface FunnelContextType {
  state: FunnelState
  setHomeType: (type: HomeType) => void
  setChallenges: (challenges: Challenge[]) => void
  toggleChallenge: (challenge: Challenge) => void
  setSelectedPackage: (pkg: PackageType) => void
  setEmail: (email: string) => void
  setFullName: (name: string) => void
  setPhone: (phone: string) => void
  setAddress: (address: string) => void
  setPreferredServiceWindow: (window: string) => void
  reset: () => void
}

const initialState: FunnelState = {
  homeType: null,
  challenges: [],
  selectedPackage: null,
  email: '',
  fullName: '',
  phone: '',
  address: '',
  preferredServiceWindow: '',
}

const FunnelContext = createContext<FunnelContextType | undefined>(undefined)

export const FunnelProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<FunnelState>(initialState)

  const setHomeType = (type: HomeType) => {
    setState(prev => ({ ...prev, homeType: type }))
  }

  const setChallenges = (challenges: Challenge[]) => {
    setState(prev => ({ ...prev, challenges }))
  }

  const toggleChallenge = (challenge: Challenge) => {
    setState(prev => {
      const hasChallenge = prev.challenges.includes(challenge)
      if (hasChallenge) {
        return { ...prev, challenges: prev.challenges.filter(c => c !== challenge) }
      }
      if (prev.challenges.length >= 3) {
        return prev
      }
      return { ...prev, challenges: [...prev.challenges, challenge] }
    })
  }

  const setSelectedPackage = (pkg: PackageType) => {
    setState(prev => ({ ...prev, selectedPackage: pkg }))
  }

  const setEmail = (email: string) => {
    setState(prev => ({ ...prev, email }))
  }

  const setFullName = (name: string) => {
    setState(prev => ({ ...prev, fullName: name }))
  }

  const setPhone = (phone: string) => {
    setState(prev => ({ ...prev, phone }))
  }

  const setAddress = (address: string) => {
    setState(prev => ({ ...prev, address }))
  }

  const setPreferredServiceWindow = (window: string) => {
    setState(prev => ({ ...prev, preferredServiceWindow: window }))
  }

  const reset = () => {
    setState(initialState)
  }

  return (
    <FunnelContext.Provider
      value={{
        state,
        setHomeType,
        setChallenges,
        toggleChallenge,
        setSelectedPackage,
        setEmail,
        setFullName,
        setPhone,
        setAddress,
        setPreferredServiceWindow,
        reset,
      }}
    >
      {children}
    </FunnelContext.Provider>
  )
}

export const useFunnel = () => {
  const context = useContext(FunnelContext)
  if (context === undefined) {
    throw new Error('useFunnel must be used within a FunnelProvider')
  }
  return context
}

